import React from "react";
import { Link } from "react-router-dom";
import { Seo } from "./Seo";
import PageHero from "./UI/PageHero";
import SectionHeading from "./UI/SectionHeading";
import Breadcrumbs, { breadcrumbSchema } from "./UI/Breadcrumbs";
import CtaBand from "./UI/CtaBand";
import deckingImg from "../assets/decking.jpg";

const projects = [
  {
    title: "Bespoke Alcove Units",
    category: "Fitted furniture",
    to: "/carpentry-services/fitted-furniture",
    img: "https://images.unsplash.com/photo-1594026112284-02bb6f3352fe?auto=format&fit=crop&w=900&q=80",
    text: "Made-to-measure shelving and cupboards either side of a chimney breast, scribed to the walls with integrated lighting and hidden cable routing for a neat living-room feature wall.",
  },
  {
    title: "Kitchen & Utility Refresh",
    category: "Kitchen fitting",
    to: "/carpentry-services/kitchen-fitting",
    img: "https://images.unsplash.com/photo-1556909212-d5b604d0c90d?auto=format&fit=crop&w=900&q=80",
    text: "A solid oak worktop, open shelving and a window bench with storage underneath, turning an underused corner into a hardworking breakfast spot.",
  },
  {
    title: "Decking & Pergola",
    category: "Outdoor carpentry",
    to: "/carpentry-services/decking-pergolas",
    img: deckingImg,
    text: "A raised deck on properly set footings with a slatted pergola for shade, finished in weatherproof oil so it stands up to the Cambridgeshire seasons.",
  },
  {
    title: "Under-Stair Storage",
    category: "Fitted furniture",
    to: "/carpentry-services/fitted-furniture",
    img: "https://images.unsplash.com/photo-1600566752355-35792bedcfea?auto=format&fit=crop&w=900&q=80",
    text: "Pull-out drawers and lift-up compartments that reclaim the awkward space beneath the stairs as organised storage for shoes, coats and bags.",
  },
  {
    title: "Home Office Fit-Out",
    category: "Fitted furniture",
    to: "/carpentry-services/fitted-furniture",
    img: "https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=900&q=80",
    text: "A built-in desk, shelving and discreet cable management fitted into a box room, making a compact but comfortable place to work from home.",
  },
  {
    title: "Staircase Renovation",
    category: "Floors & stairs",
    to: "/carpentry-services/floors-stairs",
    img: "https://images.unsplash.com/photo-1509644851169-2acc08aa25b5?auto=format&fit=crop&w=900&q=80",
    text: "New treads, a fresh handrail and turned spindles brought a tired staircase back to life and cured the creaks, without a full rebuild.",
  },
];

export default function OurWork() {
  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Our Work" },
  ];

  return (
    <>
      <Seo
        title="Our Work | Carpentry & Joinery Projects in Cambridge | Cambridge Carpenter"
        description="See recent carpentry and joinery projects across Cambridge: fitted furniture, kitchens, decking, staircases and bespoke storage built by Cambridge Carpenter."
        canonical="https://www.cambridgecarpenter.co.uk/our-work/"
        schema={breadcrumbSchema(crumbs)}
      />

      <PageHero
        eyebrow="Recent projects"
        title="Our work"
        intro="A selection of carpentry and joinery projects from homes across Cambridge and Cambridgeshire."
        crumbs={<Breadcrumbs items={crumbs} />}
      />

      <section className="max-w-site mx-auto px-5 sm:px-10 pt-20 pb-16 md:pt-28 md:pb-20">
        <div className="prose-links max-w-3xl mb-12">
          <SectionHeading
            eyebrow="Full-service carpentry experts"
            title="Recent joinery &amp; carpentry projects"
          />
          <p className="text-bark text-lg leading-8 mt-6">
            Browse projects including fitted furniture, kitchens, decking and finishing carpentry.
            Explore our <Link to="/carpentry-services">carpentry services</Link> for more detail or{" "}
            <Link to="/contact">request a quote</Link> for your own space.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((p) => (
            <Link
              key={p.title}
              to={p.to}
              className="group bg-linen border border-hairline overflow-hidden hover:border-oak transition-colors flex flex-col"
            >
              <div className="h-56 overflow-hidden">
                <img
                  src={p.img}
                  alt={p.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <span className="text-xs uppercase tracking-[0.16em] font-bold text-oak">{p.category}</span>
                <h3 className="font-display font-semibold text-xl text-ink mt-2 group-hover:text-oak transition-colors">
                  {p.title}
                </h3>
                <p className="text-bark leading-7 mt-2">{p.text}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <CtaBand />
    </>
  );
}
